import { useTranslation } from 'react-i18next';
import { MoleculeWorkspace } from '../../features/chemistry/molecule/MoleculeWorkspace';
import { ProteinWorkspace } from '../../features/biology/protein/ProteinWorkspace';
import { GeneWorkspace } from '../../features/biology/gene/GeneWorkspace';
import { VariantWorkspace } from '../../features/genomics/variant/VariantWorkspace';
import { TranscriptWorkspace } from '../../features/genomics/transcript/TranscriptWorkspace';
import { PeptideWorkspace } from '../../features/chemistry/peptide/PeptideWorkspace';
import type { ScientificObjectRef } from '../ScientificObjectRef';
import { getScientificObjectSummary } from '../scientificObjectApi';
import { useAsyncResource } from '../../hooks/useAsyncResource';
import { SiteHeader } from '../../app/SiteHeader';

interface ObjectWorkspaceProps {
  readonly reference: ScientificObjectRef;
}

export function ObjectWorkspace(props: ObjectWorkspaceProps) {
  return (
    <div className="object-page">
      <SiteHeader />
      <ObjectWorkspaceContent {...props} />
    </div>
  );
}

function ObjectWorkspaceContent({ reference }: ObjectWorkspaceProps) {
  const { t } = useTranslation();
  const state = useAsyncResource(
    () => getScientificObjectSummary(reference),
    [reference.id, reference.type],
    'Unable to load this scientific object.'
  );

  if (state.status === 'loading')
    return (
      <main className="object-workspace-status">
        <p>{t('objectWorkspace.loading')}</p>
      </main>
    );
  if (state.status === 'error')
    return (
      <main className="object-workspace-status">
        <p role="alert">{state.message}</p>
      </main>
    );

  const summary = state.value;
  switch (reference.type) {
    case 'MOLECULE':
      return <MoleculeWorkspace reference={reference} summary={summary} />;
    case 'PROTEIN':
      return <ProteinWorkspace reference={reference} summary={summary} />;
    case 'GENE':
      return <GeneWorkspace reference={reference} summary={summary} />;
    case 'VARIANT':
      return <VariantWorkspace reference={reference} summary={summary} />;
    case 'TRANSCRIPT':
      return <TranscriptWorkspace reference={reference} summary={summary} />;
    case 'PEPTIDE':
      return <PeptideWorkspace reference={reference} summary={summary} />;
  }
}
